import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { JOB } from '@/constants/testIds';
import { toast, Toaster } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, PoundSterling, TrendingUp, TrendingDown, FileText, Download, Printer, X, Camera, Fuel } from 'lucide-react';
import JobInvoice from '@/components/JobInvoice';
import JobReport from '@/components/JobReport';
import Seo from '@/components/Seo';

const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const COST_TYPES = { materials: 'Materials', labour: 'Labour', subcontractor: 'Subcontractor', plant: 'Plant/Hire', overheads: 'Overheads', depreciation: 'Depreciation/Repair', fuel: 'Fuel' };

export default function JobDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [meta, setMeta] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const invoiceRef = useRef(null);
  const reportRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/jobs/${id}`);
      setJob(r.data);
      setMeta({
        name: r.data.name, customer_name: r.data.customer_name || '', quote_id: r.data.quote_id || null,
        quoted_price: r.data.quoted_price || 0, est_materials: r.data.est_materials || 0, est_labour: r.data.est_labour || 0,
        est_overheads: r.data.est_overheads || 0, est_depreciation: r.data.est_depreciation || 0, est_fuel: r.data.est_fuel || 0,
        expected_profit: r.data.expected_profit || 0, vat_rate: r.data.vat_rate || 0,
        status: r.data.status || 'in_progress', percent_complete: r.data.percent_complete || 0,
        start_date: r.data.start_date || '', target_end_date: r.data.target_end_date || '',
        site_postcode: r.data.site_postcode || '',
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail)); nav('/jobs');
    }
  };
  useEffect(() => {
    load();
    api.get('/me/logo').then(({ data }) => setLogoUrl(data.data_url)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const saveMeta = async (patch) => {
    const body = { ...meta, ...patch };
    setMeta(body);
    try { const r = await api.put(`/jobs/${id}`, body); setJob(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/jobs/${id}`, {
        ...meta,
        quoted_price: parseFloat(meta.quoted_price) || 0,
        est_materials: parseFloat(meta.est_materials) || 0,
        est_labour: parseFloat(meta.est_labour) || 0,
        est_overheads: parseFloat(meta.est_overheads) || 0,
        est_depreciation: parseFloat(meta.est_depreciation) || 0,
        est_fuel: parseFloat(meta.est_fuel) || 0,
        expected_profit: parseFloat(meta.expected_profit) || 0,
        vat_rate: parseFloat(meta.vat_rate) || 0,
        percent_complete: parseInt(meta.percent_complete) || 0,
      });
      setJob(r.data);
      toast.success('Job saved');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeJob = async () => {
    if (!window.confirm('Delete this job and all its cost entries?')) return;
    try { await api.delete(`/jobs/${id}`); toast.success('Job deleted'); nav('/jobs'); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const invoiceNumber = job ? `INV-${String(job.id).slice(-6).toUpperCase()}` : '';
  const reportNumber = job ? `JOB-${String(job.id).slice(-6).toUpperCase()}` : '';

  const printInvoice = () => { setShowInvoice(false); setTimeout(() => window.print(), 150); };
  const captureToPdf = async (node, filename) => {
    if (!node) { toast.error('Nothing to export'); return; }
    setDownloading(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      await html2pdf().set({
        margin: [10, 8, 10, 8], filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 1024 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      }).from(node).save();
      toast.success('Downloaded');
    } catch (e) { toast.error('Could not generate PDF — try Print instead'); }
    finally { setDownloading(false); }
  };
  const downloadInvoice = () => captureToPdf(invoiceRef.current?.querySelector('.print-page'), `${invoiceNumber}.pdf`);
  const downloadReport = () => captureToPdf(reportRef.current?.querySelector('.print-page'), `${reportNumber}.pdf`);

  if (!job || !meta) return <div className="max-w-5xl mx-auto px-4 py-12 font-mono text-slate-500">Loading…</div>;

  const profitPos = job.profit >= 0;
  const labourSpent = job.cost_by_type?.labour || 0;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo title={job?.name ? `${job.name} — Job` : 'Job'} noindex />
      <Toaster position="top-center" richColors />

      {/* Hidden invoice + report for print + PDF capture */}
      <div ref={invoiceRef} className="pdf-capture-host" aria-hidden="true">
        <JobInvoice user={user} logoUrl={logoUrl} job={job} invoiceNumber={invoiceNumber} />
      </div>
      <div ref={reportRef} className="pdf-capture-host" aria-hidden="true">
        <JobReport user={user} logoUrl={logoUrl} job={job} reportNumber={reportNumber} />
      </div>

      <Link to="/jobs" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-600 hover:text-brand mb-4 font-semibold"><ArrowLeft size={14}/> All jobs</Link>

      {/* Header */}
      <div className="tm-card p-5 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[200px]">
            <input className="font-display font-semibold text-2xl sm:text-3xl uppercase tracking-tight bg-transparent w-full focus:outline-none border-b border-transparent focus:border-brand"
              value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} onBlur={(e) => saveMeta({ name: e.target.value })} />
            <input className="text-sm text-slate-500 bg-transparent w-full mt-1 focus:outline-none" placeholder="Customer name"
              value={meta.customer_name} onChange={(e) => setMeta({ ...meta, customer_name: e.target.value })} onBlur={(e) => saveMeta({ customer_name: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <button data-testid={JOB.save} onClick={saveAll} disabled={saving} className="tm-btn text-xs min-h-0 py-2"><Save size={14}/> {saving ? 'Saving…' : 'Save'}</button>
            <button data-testid="job-report-btn" onClick={() => setShowReport(true)} className="tm-btn text-xs min-h-0 py-2"><FileText size={14}/> Report</button>
            <button data-testid="job-invoice-btn" onClick={() => setShowInvoice(true)} className="tm-btn text-xs min-h-0 py-2"><FileText size={14}/> Invoice</button>
            <select data-testid={JOB.status} value={meta.status} onChange={(e) => saveMeta({ status: e.target.value })} className="tm-input bg-white w-auto min-h-0 py-2 text-sm">
              <option value="not_started">Not started</option><option value="in_progress">In progress</option><option value="complete">Complete</option>
            </select>
            <button data-testid={JOB.delete} onClick={removeJob} className="tm-btn tm-btn-ghost text-xs min-h-0 py-2"><Trash2 size={14}/></button>
          </div>
        </div>

        {/* editable baseline + dates */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <label className="block"><span className="tm-label">Quoted price (£)</span>
            <input type="number" className="tm-input" value={meta.quoted_price} onChange={(e) => setMeta({ ...meta, quoted_price: e.target.value })} onBlur={(e) => saveMeta({ quoted_price: parseFloat(e.target.value) || 0 })} /></label>
          <label className="block"><span className="tm-label">Est. materials (£)</span>
            <input type="number" className="tm-input" value={meta.est_materials} onChange={(e) => setMeta({ ...meta, est_materials: e.target.value })} onBlur={(e) => saveMeta({ est_materials: parseFloat(e.target.value) || 0 })} /></label>
          <label className="block"><span className="tm-label">Est. labour (£)</span>
            <input type="number" className="tm-input" value={meta.est_labour} onChange={(e) => setMeta({ ...meta, est_labour: e.target.value })} onBlur={(e) => saveMeta({ est_labour: parseFloat(e.target.value) || 0 })} /></label>
          <label className="block"><span className="tm-label">Overhead cost (£)</span>
            <input type="number" className="tm-input" value={meta.est_overheads} onChange={(e) => setMeta({ ...meta, est_overheads: e.target.value })} onBlur={(e) => saveMeta({ est_overheads: parseFloat(e.target.value) || 0 })} /></label>
          <label className="block"><span className="tm-label">Depreciation &amp; repair (£)</span>
            <input type="number" className="tm-input" value={meta.est_depreciation} onChange={(e) => setMeta({ ...meta, est_depreciation: e.target.value })} onBlur={(e) => saveMeta({ est_depreciation: parseFloat(e.target.value) || 0 })} /></label>
          <label className="block"><span className="tm-label">Fuel cost (£)</span>
            <input type="number" className="tm-input" value={meta.est_fuel} onChange={(e) => setMeta({ ...meta, est_fuel: e.target.value })} onBlur={(e) => saveMeta({ est_fuel: parseFloat(e.target.value) || 0 })} /></label>
          <label className="block"><span className="tm-label">Expected profit (£)</span>
            <input type="number" className="tm-input" value={meta.expected_profit} onChange={(e) => setMeta({ ...meta, expected_profit: e.target.value })} onBlur={(e) => saveMeta({ expected_profit: parseFloat(e.target.value) || 0 })} /></label>
          <label className="block"><span className="tm-label">VAT rate (%)</span>
            <input type="number" className="tm-input" value={meta.vat_rate} onChange={(e) => setMeta({ ...meta, vat_rate: e.target.value })} onBlur={(e) => saveMeta({ vat_rate: parseFloat(e.target.value) || 0 })} /></label>
          <label className="block"><span className="tm-label">% Complete</span>
            <input data-testid={JOB.percent} type="number" min="0" max="100" className="tm-input" value={meta.percent_complete} onChange={(e) => setMeta({ ...meta, percent_complete: e.target.value })} onBlur={(e) => saveMeta({ percent_complete: parseInt(e.target.value) || 0 })} /></label>
          <label className="block"><span className="tm-label">Start date</span>
            <input type="date" className="tm-input" value={meta.start_date || ''} onChange={(e) => saveMeta({ start_date: e.target.value })} /></label>
          <label className="block"><span className="tm-label">Target end</span>
            <input type="date" className="tm-input" value={meta.target_end_date || ''} onChange={(e) => saveMeta({ target_end_date: e.target.value })} /></label>
          <label className="block sm:col-span-2"><span className="tm-label">Site postcode</span>
            <input type="text" className="tm-input" placeholder="e.g. M1 1AE" value={meta.site_postcode || ''} data-testid="job-site-postcode"
              onChange={(e) => setMeta({ ...meta, site_postcode: e.target.value })} onBlur={(e) => saveMeta({ site_postcode: e.target.value })} /></label>
        </div>
      </div>

      {/* Cheapest fuel near this job */}
      <FuelNearJob postcode={job.site_postcode} />

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="job-kpis">
        <Kpi label="Quoted (incl VAT)" value={gbp(job.quoted_price)} />
        <Kpi label="Net (ex VAT)" value={gbp(job.net_price)} />
        <Kpi label={`VAT (${job.vat_rate || 0}%)`} value={gbp(job.vat_amount)} />
        <Kpi label="Cost so far" value={gbp(job.total_cost)} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi label="Expected profit" value={gbp(job.expected_profit)} hint="What you planned to make on this job." />
        <Kpi label="Margin" value={`${job.margin}%`} accent={profitPos ? 'green' : 'red'} hint="Real profit as a % of the ex-VAT price." />
        <Kpi label="Real profit (after VAT & costs)" value={gbp(job.profit)} accent={profitPos ? 'green' : 'red'} icon={profitPos ? <TrendingUp size={16}/> : <TrendingDown size={16}/>} hint="VAT and every logged cost taken out — your true profit." />
        <Kpi label="Vs expected" value={`${job.profit_vs_expected >= 0 ? '+' : ''}${gbp(job.profit_vs_expected)}`} accent={job.profit_vs_expected >= 0 ? 'green' : 'red'} hint="How much you beat (or missed) your expected profit by." />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi label="Received" value={gbp(job.received)} />
        <Kpi label="Outstanding" value={gbp(job.owed ?? job.outstanding)} icon={<PoundSterling size={16}/>} accent={(job.owed ?? job.outstanding) <= 0 ? 'green' : undefined} hint={job.credit > 0 ? `Paid in full — customer is £${job.credit.toFixed(2)} in credit (overpaid).` : 'Still owed by the customer on this job.'} />
        <Kpi label="VAT on sales (output)" value={gbp(job.vat_amount)} hint="VAT you charged the customer." />
        <Kpi label="VAT on costs (input)" value={gbp(job.input_vat)} hint="Reclaimable VAT within your logged costs." />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi label="Net VAT owed to HMRC" value={gbp(job.net_vat)} accent={job.net_vat >= 0 ? undefined : 'green'} hint="Output VAT − input VAT for this job." />
      </div>

      {/* Budget bars */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <BudgetBar label="Materials" spent={job.cost_by_type?.materials || 0} budget={job.est_materials} />
        <BudgetBar label="Labour" spent={labourSpent} budget={job.est_labour} />
        <BudgetBar label="Overheads" spent={job.cost_by_type?.overheads || 0} budget={job.est_overheads} />
        <BudgetBar label="Depreciation/Repair" spent={job.cost_by_type?.depreciation || 0} budget={job.est_depreciation} />
        <BudgetBar label="Fuel" spent={job.cost_by_type?.fuel || 0} budget={job.est_fuel} />
      </div>

      <BreakdownCard job={job} />

      <CostSection job={job} onChange={setJob} jobId={id} />
      <PaymentSection job={job} onChange={setJob} jobId={id} />
      <DiarySection job={job} onChange={setJob} jobId={id} />

      {showInvoice && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4 print:hidden" onClick={() => setShowInvoice(false)}>
          <div className="bg-white max-w-3xl w-full my-6" onClick={(e) => e.stopPropagation()} data-testid="job-invoice-modal">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white">
              <h3 className="font-display font-bold text-lg uppercase">Invoice preview</h3>
              <div className="flex items-center gap-2">
                <button data-testid="job-invoice-download" onClick={downloadInvoice} disabled={downloading} className="tm-btn text-xs min-h-0 py-2 px-3"><Download size={14}/> {downloading ? 'Preparing…' : 'Download'}</button>
                <button data-testid="job-invoice-print" onClick={printInvoice} className="tm-btn tm-btn-dark text-xs min-h-0 py-2 px-3"><Printer size={14}/> Print</button>
                <button onClick={() => setShowInvoice(false)} className="text-slate-500 hover:text-slate-900"><X size={20}/></button>
              </div>
            </div>
            <div className="pdf-preview-wrap p-4 max-h-[75vh] overflow-y-auto">
              <JobInvoice user={user} logoUrl={logoUrl} job={job} invoiceNumber={invoiceNumber} />
            </div>
          </div>
        </div>
      )}

      {showReport && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4 print:hidden" onClick={() => setShowReport(false)}>
          <div className="bg-white max-w-3xl w-full my-6" onClick={(e) => e.stopPropagation()} data-testid="job-report-modal">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white">
              <h3 className="font-display font-bold text-lg uppercase">Job report (internal)</h3>
              <div className="flex items-center gap-2">
                <button data-testid="job-report-download" onClick={downloadReport} disabled={downloading} className="tm-btn text-xs min-h-0 py-2 px-3"><Download size={14}/> {downloading ? 'Preparing…' : 'Download'}</button>
                <button onClick={() => setShowReport(false)} className="text-slate-500 hover:text-slate-900"><X size={20}/></button>
              </div>
            </div>
            <div className="pdf-preview-wrap p-4 max-h-[75vh] overflow-y-auto">
              <JobReport user={user} logoUrl={logoUrl} job={job} reportNumber={reportNumber} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FuelNearJob({ postcode }) {
  const [state, setState] = useState({ loading: false, stations: null, resolved: '' });
  const [fuel, setFuel] = useState('B7'); // diesel default for vans
  useEffect(() => {
    let cancelled = false;
    const pc = (postcode || '').trim();
    if (!pc) { setState({ loading: false, stations: null, resolved: '' }); return; }
    (async () => {
      setState((s) => ({ ...s, loading: true }));
      try {
        const g = await api.get('/geo/postcode', { params: { q: pc } });
        const r = await api.get('/fuel/stations', { params: { lat: g.data.lat, lng: g.data.lng, radius: 5, fuel } });
        if (!cancelled) setState({ loading: false, stations: r.data.stations || [], resolved: g.data.postcode });
      } catch (e) {
        if (!cancelled) setState({ loading: false, stations: [], resolved: '' });
      }
    })();
    return () => { cancelled = true; };
  }, [postcode, fuel]);

  if (!postcode || !postcode.trim()) {
    return (
      <section className="tm-card p-5 mb-6 border-dashed" data-testid="fuel-near-job-empty">
        <div className="flex items-center gap-2 text-slate-500">
          <Fuel size={18} className="text-brand" />
          <p className="text-sm">Add a <strong>site postcode</strong> above to see the cheapest fuel near this job.</p>
        </div>
      </section>
    );
  }

  const top = (state.stations || []).slice(0, 3);
  return (
    <section className="tm-card p-5 mb-6" data-testid="fuel-near-job">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-display font-bold text-lg uppercase flex items-center gap-2"><Fuel size={18} className="text-brand"/> Cheapest fuel near site</h3>
        <div className="flex items-center gap-2">
          <div className="flex">
            {[['B7', 'Diesel'], ['E10', 'Unleaded']].map(([k, l]) => (
              <button key={k} onClick={() => setFuel(k)} data-testid={`fuel-near-toggle-${k}`}
                className={`px-2.5 py-1 text-xs font-mono uppercase border ${fuel === k ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-300'}`}>{l}</button>
            ))}
          </div>
          <a href="/fuel" target="_blank" rel="noreferrer" className="text-[11px] font-mono text-brand hover:underline">Full map &rarr;</a>
        </div>
      </div>
      {state.loading ? (
        <p className="font-mono text-sm text-slate-400">Finding fuel near {postcode}&hellip;</p>
      ) : top.length === 0 ? (
        <p className="text-sm text-slate-400">No stations found near {postcode}. Check the postcode is correct.</p>
      ) : (
        <div className="space-y-2">
          {top.map((s, i) => (
            <div key={s.site_id} className={`flex items-center gap-3 p-2 rounded ${i === 0 ? 'bg-emerald-50 border border-emerald-200' : ''}`} data-testid={`fuel-near-station-${i}`}>
              <span className={`font-display font-semibold text-lg w-20 text-center ${i === 0 ? 'text-emerald-600' : 'text-slate-900'}`}>{s[fuel] != null ? `${Number(s[fuel]).toFixed(1)}p` : '—'}</span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{s.brand} {i === 0 && <span className="text-[10px] uppercase text-emerald-600 font-semibold">&middot; cheapest</span>}</p>
                <p className="text-xs text-slate-500 truncate">{s.address}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-xs font-bold text-slate-700">{s.distance} mi</div>
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-brand hover:underline">Directions &rarr;</a>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function Kpi({ label, value, accent, icon, hint }) {
  const color = accent === 'green' ? 'text-emerald-600' : accent === 'red' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="tm-card p-4">
      <p className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1 font-semibold">{icon && <span className="text-brand">{icon}</span>}{label}</p>
      <p className={`font-display font-semibold text-2xl mt-1 ${color}`}>{value}</p>
      {hint && <p className="text-[11px] leading-snug text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function BudgetBar({ label, spent, budget }) {
  const pct = budget > 0 ? Math.round((spent / budget) * 100) : 0;
  const over = budget > 0 && spent > budget;
  return (
    <div className="tm-card p-4">
      <div className="flex justify-between text-sm mb-2">
        <span className="uppercase tracking-wide text-slate-600 font-semibold">{label}</span>
        <span className={`font-mono font-bold ${over ? 'text-rose-600' : 'text-slate-900'}`}>{gbp(spent)} {budget > 0 && <span className="text-slate-400">/ {gbp(budget)}</span>}</span>
      </div>
      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${over ? 'bg-rose-500' : 'bg-brand'}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <p className="text-[11px] font-mono text-slate-500 mt-1">{budget > 0 ? `${pct}% of estimate${over ? ' — over budget' : ''}` : 'No estimate set'}</p>
    </div>
  );
}

function BreakdownCard({ job }) {
  const segs = [
    { key: 'est_materials', label: 'Materials', color: 'var(--tm-orange)' },
    { key: 'est_labour', label: 'Labour', color: '#2563EB' },
    { key: 'est_overheads', label: 'Overheads', color: '#7C3AED' },
    { key: 'est_depreciation', label: 'Depreciation/Repair', color: '#0891B2' },
    { key: 'est_fuel', label: 'Fuel', color: '#CA8A04' },
    { key: 'expected_profit', label: 'Expected profit', color: '#059669' },
    { key: 'vat_amount', label: 'VAT', color: '#475569' },
  ].map((s) => ({ ...s, value: Number(job[s.key] || 0) }));
  const quoted = Number(job.quoted_price || 0);
  const allocated = segs.reduce((a, s) => a + s.value, 0);
  const unalloc = Math.round((quoted - allocated) * 100) / 100;
  const all = [...segs];
  if (unalloc > 0.01) all.push({ key: 'unallocated', label: 'Unallocated', value: unalloc, color: '#94A3B8' });
  const base = quoted > 0 ? quoted : allocated;
  const shown = all.filter((s) => s.value > 0);

  if (base <= 0 || shown.length === 0) {
    return (
      <section className="tm-card p-5 mb-6">
        <h3 className="font-display font-bold text-lg uppercase mb-2">Quote Breakdown</h3>
        <p className="text-sm text-slate-400 font-mono">Set your estimate fields above to see how the quoted price splits.</p>
      </section>
    );
  }

  return (
    <section className="tm-card p-5 mb-6">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-display font-bold text-lg uppercase">Quote Breakdown</h3>
        <span className="font-mono text-sm text-slate-500">of {gbp(base)}</span>
      </div>
      <div className="flex h-5 w-full rounded-full overflow-hidden mb-4 border border-slate-200" data-testid="job-breakdown-bar">
        {shown.map((s) => (
          <div key={s.key} title={`${s.label}: ${gbp(s.value)}`} style={{ width: `${(s.value / base) * 100}%`, background: s.color }} />
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {shown.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: s.color }} />
            <span className="text-slate-600 flex-1 truncate">{s.label}</span>
            <span className="font-mono font-bold">{gbp(s.value)}</span>
            <span className="font-mono text-xs text-slate-400 w-10 text-right">{Math.round((s.value / base) * 100)}%</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function CostSection({ job, onChange, jobId }) {
  const [f, setF] = useState({ type: 'materials', description: '', supplier: '', amount: '', vat_amount: '', labour_mode: 'hourly', hours: '', rate: '', days: '', day_rate: '', date: '', receipt_path: null, receipt_filename: null });
  const [scanning, setScanning] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  // Auto-fill VAT at 20% of net when a gross amount is typed (VAT-registered default), unless user edits VAT.
  const onAmount = (v) => {
    setF((s) => {
      const gross = parseFloat(v) || 0;
      const autoVat = gross > 0 ? Math.round((gross - gross / 1.2) * 100) / 100 : '';
      return { ...s, amount: v, vat_amount: s.vatTouched ? s.vat_amount : (autoVat === '' ? '' : String(autoVat)) };
    });
  };
  const onScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/receipts/scan', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const ex = r.data.extracted || {};
      setF((s) => ({
        ...s,
        type: (['materials', 'plant', 'fuel', 'subcontractor', 'overheads'].includes(ex.category) ? ex.category : s.type),
        description: ex.supplier || s.description,
        supplier: ex.supplier || s.supplier,
        amount: ex.amount ? String(ex.amount) : s.amount,
        vat_amount: (ex.vat_amount != null ? String(ex.vat_amount) : s.vat_amount),
        vatTouched: true,
        date: ex.date || s.date,
        receipt_path: r.data.receipt_path,
        receipt_filename: r.data.receipt_filename,
      }));
      toast.success(`Receipt scanned${ex.supplier ? ` — ${ex.supplier}` : ''}. Please check the figures.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Could not scan receipt');
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  const add = async () => {
    if (f.type === 'labour') {
      const hasHourly = f.labour_mode === 'hourly' && f.hours;
      const hasDay = f.labour_mode === 'day' && f.days;
      if (!hasHourly && !hasDay && !f.amount) { toast.error('Enter hours/days or an amount'); return; }
    } else if (!f.amount) { toast.error('Enter an amount'); return; }
    try {
      const r = await api.post(`/jobs/${jobId}/costs`, {
        type: f.type, description: f.description, supplier: f.supplier, amount: parseFloat(f.amount) || 0,
        vat_amount: parseFloat(f.vat_amount) || 0,
        labour_mode: f.labour_mode, hours: parseFloat(f.hours) || 0, rate: parseFloat(f.rate) || 0,
        days: parseFloat(f.days) || 0, day_rate: parseFloat(f.day_rate) || 0, date: f.date || null,
        receipt_path: f.receipt_path, receipt_filename: f.receipt_filename,
      });
      onChange(r.data); setF({ type: f.type, description: '', supplier: '', amount: '', vat_amount: '', labour_mode: f.labour_mode, hours: '', rate: '', days: '', day_rate: '', date: '', receipt_path: null, receipt_filename: null });
      toast.success('Cost added');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (eid) => { try { const r = await api.delete(`/jobs/${jobId}/costs/${eid}`); onChange(r.data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const costs = job.costs || [];
  const isLabour = f.type === 'labour';
  const receiptSrc = (p) => `${api.defaults.baseURL}/receipts/file/${p}`;

  return (
    <section className="tm-card p-5 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-display font-bold text-lg uppercase">Costs &amp; Expenses</h3>
        <div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onScan} data-testid="job-receipt-input" />
          <button onClick={() => fileRef.current?.click()} disabled={scanning} className="tm-btn tm-btn-ghost text-xs min-h-0 py-2" data-testid="job-scan-receipt-btn">
            <Camera size={14}/> {scanning ? 'Scanning…' : 'Scan receipt'}
          </button>
        </div>
      </div>
      {f.receipt_path && (
        <div className="flex items-center gap-2 mb-3 text-xs font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="job-receipt-attached">
          <img src={receiptSrc(f.receipt_path)} alt="receipt" className="w-10 h-10 object-cover border border-slate-300" />
          Receipt attached — review the figures below, then Add cost.
          <button onClick={() => set('receipt_path', null)} className="ml-auto text-slate-400 hover:text-rose-600"><X size={14}/></button>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3">
        <select className="tm-input bg-white" value={f.type} onChange={(e) => set('type', e.target.value)} data-testid="job-cost-type">
          {Object.entries(COST_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input className="tm-input sm:col-span-2" placeholder="Description" value={f.description} onChange={(e) => set('description', e.target.value)} data-testid="job-cost-desc" />
        {isLabour ? (
          <select className="tm-input bg-white" value={f.labour_mode} onChange={(e) => set('labour_mode', e.target.value)} data-testid="job-cost-labour-mode">
            <option value="hourly">Hourly</option>
            <option value="day">Day rate</option>
          </select>
        ) : (
          <input className="tm-input" placeholder="Supplier" value={f.supplier} onChange={(e) => set('supplier', e.target.value)} />
        )}
        {isLabour && f.labour_mode === 'hourly' && (
          <>
            <input className="tm-input" type="number" placeholder="Hours" value={f.hours} onChange={(e) => set('hours', e.target.value)} data-testid="job-cost-hours" />
            <input className="tm-input" type="number" placeholder="Rate £/h" value={f.rate} onChange={(e) => set('rate', e.target.value)} data-testid="job-cost-rate" />
          </>
        )}
        {isLabour && f.labour_mode === 'day' && (
          <>
            <input className="tm-input" type="number" placeholder="Days" value={f.days} onChange={(e) => set('days', e.target.value)} data-testid="job-cost-days" />
            <input className="tm-input" type="number" placeholder="Rate £/day" value={f.day_rate} onChange={(e) => set('day_rate', e.target.value)} data-testid="job-cost-day-rate" />
          </>
        )}
        {!isLabour && (
          <input className="tm-input" type="number" placeholder="£ Gross (incl VAT)" value={f.amount} onChange={(e) => onAmount(e.target.value)} data-testid="job-cost-amount" />
        )}
        {isLabour && (
          <input className="tm-input" type="number" placeholder="Or £ total" value={f.amount} onChange={(e) => onAmount(e.target.value)} data-testid="job-cost-amount" />
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3">
        <label className="block sm:col-span-2">
          <span className="tm-label">VAT within amount (£)</span>
          <input className="tm-input" type="number" placeholder="0.00" value={f.vat_amount} onChange={(e) => setF((s) => ({ ...s, vat_amount: e.target.value, vatTouched: true }))} data-testid="job-cost-vat" />
        </label>
        <label className="block sm:col-span-2">
          <span className="tm-label">Date</span>
          <input className="tm-input" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} data-testid="job-cost-date" />
        </label>
      </div>
      <button data-testid={JOB.costAdd} onClick={add} className="tm-btn text-xs min-h-0 py-2 mb-4"><Plus size={14}/> Add cost</button>

      {costs.length === 0 ? <p className="text-sm text-slate-400 font-mono">No costs logged yet.</p> : (
        <div className="divide-y divide-slate-200">
          {costs.map((c) => (
            <div key={c.id} className="py-2 flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0 flex items-center gap-2">
                {c.receipt_path && (
                  <a href={receiptSrc(c.receipt_path)} target="_blank" rel="noreferrer" title="View receipt">
                    <img src={receiptSrc(c.receipt_path)} alt="receipt" className="w-9 h-9 object-cover border border-slate-300 shrink-0" />
                  </a>
                )}
                <div className="min-w-0">
                  <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5 mr-2 font-semibold">{COST_TYPES[c.type] || c.type}</span>
                  <span className="font-medium">{c.description || '—'}</span>
                  {c.supplier && <span className="text-slate-400"> · {c.supplier}</span>}
                  {c.type === 'labour' && (c.labour_mode === 'day' && c.days ? <span className="text-slate-400"> · {c.days} days @ £{c.day_rate}/day</span> : (c.hours ? <span className="text-slate-400"> · {c.hours}h @ £{c.rate}</span> : null))}
                  <span className="text-slate-400 block text-xs">{c.date}{c.vat_amount > 0 ? ` · incl £${Number(c.vat_amount).toFixed(2)} VAT` : ''}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-mono font-bold">{gbp(c.amount)}</span>
                <button onClick={() => del(c.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function PaymentSection({ job, onChange, jobId }) {
  const [f, setF] = useState({ description: '', amount: '', date: '' });
  const add = async () => {
    if (!f.amount) { toast.error('Enter an amount'); return; }
    try { const r = await api.post(`/jobs/${jobId}/payments`, { description: f.description, amount: parseFloat(f.amount) || 0, date: f.date || null }); onChange(r.data); setF({ description: '', amount: '', date: '' }); toast.success('Payment added'); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (pid) => { try { const r = await api.delete(`/jobs/${jobId}/payments/${pid}`); onChange(r.data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const payments = job.payments || [];
  return (
    <section className="tm-card p-5 mb-6">
      <h3 className="font-display font-bold text-lg uppercase mb-3">Payments Received</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        <input className="tm-input sm:col-span-2" placeholder="e.g. Deposit / Stage 1" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="job-payment-desc" />
        <input className="tm-input" type="number" placeholder="£ Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="job-payment-amount" />
      </div>
      <button data-testid={JOB.paymentAdd} onClick={add} className="tm-btn text-xs min-h-0 py-2 mb-4"><Plus size={14}/> Add payment</button>
      {payments.length === 0 ? <p className="text-sm text-slate-400 font-mono">No payments recorded.</p> : (
        <div className="divide-y divide-slate-200">
          {payments.map((p) => (
            <div key={p.id} className="py-2 flex items-center justify-between text-sm">
              <div><span className="font-medium">{p.description || 'Payment'}</span><span className="text-slate-400 block text-xs">{p.date}</span></div>
              <div className="flex items-center gap-3"><span className="font-mono font-bold text-emerald-600">{gbp(p.amount)}</span><button onClick={() => del(p.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14}/></button></div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DiarySection({ job, onChange, jobId }) {
  const [note, setNote] = useState('');
  const add = async () => {
    if (!note.trim()) return;
    try { const r = await api.post(`/jobs/${jobId}/diary`, { note }); onChange(r.data); setNote(''); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (nid) => { try { const r = await api.delete(`/jobs/${jobId}/diary/${nid}`); onChange(r.data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const diary = [...(job.diary || [])].reverse();
  return (
    <section className="tm-card p-5">
      <h3 className="font-display font-bold text-lg uppercase mb-3">Site Diary / Notes</h3>
      <div className="flex gap-2 mb-4">
        <input className="tm-input flex-1" placeholder="Log a note (delays, variations, materials ordered…)" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} data-testid="job-diary-input" />
        <button data-testid={JOB.diaryAdd} onClick={add} className="tm-btn text-xs min-h-0 py-2"><Plus size={14}/> Log</button>
      </div>
      {diary.length === 0 ? <p className="text-sm text-slate-400 font-mono">No diary entries yet.</p> : (
        <ul className="space-y-2">
          {diary.map((d) => (
            <li key={d.id} className="flex items-start justify-between gap-3 text-sm border-l border-brand pl-3 py-1">
              <div><p>{d.note}</p><p className="text-[11px] font-mono text-slate-400">{new Date(d.ts).toLocaleString('en-GB')}</p></div>
              <button onClick={() => del(d.id)} className="text-slate-400 hover:text-rose-600 shrink-0"><Trash2 size={14}/></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
